// ========================================
// MESSAGES MANAGER
// Gestionnaire des messages de contact
// ========================================

import React, { useState } from 'react';
import AdminLayout from '../../../components/admin/Layout/AdminLayout';
import useMessages from '../../../hooks/useMessages';
import { formatDate, formatRelativeDate } from '../../../utils';
import {
  FiMail,
  FiCheckCircle,
  FiTrash2,
  FiLoader,
  FiSearch,
  FiX,
  FiUser,
  FiClock,
  FiChevronRight,
  FiInbox,
  FiArrowLeft
} from 'react-icons/fi';
import './MessagesManager.css';

const MessagesManager = () => {
  const { messages, loading, unreadCount, markMessageAsRead, removeMessage } = useMessages();
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  // ✅ NOUVEAU: État pour gérer la vue mobile
  const [showDetail, setShowDetail] = useState(false);

  // Filtrer les messages
  const filteredMessages = messages.filter(msg =>
    msg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sélectionner un message
  const handleSelectMessage = async (message) => {
    setSelectedMessage(message);
    setShowDetail(true); // ✅ Afficher le détail sur mobile
    if (!message.isRead) {
      await markMessageAsRead(message.id);
    }
  };

  // ✅ NOUVEAU: Retourner à la liste sur mobile
  const handleBackToList = () => {
    setShowDetail(false);
  };

  // Supprimer un message
  const handleDelete = async (messageId) => {
    await removeMessage(messageId);
    setShowDeleteConfirm(null);
    if (selectedMessage?.id === messageId) {
      setSelectedMessage(null);
      setShowDetail(false); // ✅ Retourner à la liste après suppression
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="manager-loading">
          <FiLoader className="spinner" />
          <p>Chargement des messages...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="messages-manager">
        {/* Header */}
        <div className="manager-header">
          <div className="list-header-left">
            <h1>Messages</h1>
            {unreadCount > 0 && (
              <span className="unread-badge">{unreadCount} non lu{unreadCount > 1 ? 's' : ''}</span>
            )}
          </div>
        </div>

        <div className={`messages-layout ${showDetail ? 'show-detail' : ''}`}>
          {/* Liste des messages */}
          <div className="messages-list-panel">
            {/* Recherche */}
            <div className="messages-search">
              <FiSearch />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')}>
                  <FiX />
                </button>
              )}
            </div>

            {/* Liste */}
            {filteredMessages.length === 0 ? (
              <div className="messages-empty">
                <FiInbox />
                <p>Aucun message</p>
              </div>
            ) : (
              <div className="messages-list">
                {filteredMessages.map(message => (
                  <div
                    key={message.id}
                    className={`message-item ${!message.isRead ? 'unread' : ''} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                    onClick={() => handleSelectMessage(message)}
                  >
                    <div className="message-status">
                      {message.isRead ? <FiCheckCircle /> : <FiMail />}
                    </div>
                    <div className="message-preview">
                      <div className="message-sender">
                        <strong>{message.name}</strong>
                        <span className="message-time">
                          {formatRelativeDate(message.createdAt)}
                        </span>
                      </div>
                      <div className="message-email">{message.email}</div>
                      <div className="message-excerpt">
                        {message.message?.substring(0, 80)}...
                      </div>
                    </div>
                    <FiChevronRight className="message-arrow" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Détail du message */}
          <div className="message-detail-panel">
            {selectedMessage ? (
              <div className="message-detail">
                <div className="message-detail-header">
                  {/* ✅ NOUVEAU: Bouton retour sur mobile */}
                  <button
                    className="btn btn-ghost btn-back-mobile"
                    onClick={handleBackToList}
                  >
                    <FiArrowLeft />
                    <span>Retour</span>
                  </button>
                  
                  <div className="sender-info">
                    <div className="sender-avatar">
                      <FiUser />
                    </div>
                    <div>
                      <h3>{selectedMessage.name}</h3>
                      <a href={`mailto:${selectedMessage.email}`}>
                        {selectedMessage.email}
                      </a>
                    </div>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm btn-delete"
                    onClick={() => setShowDeleteConfirm(selectedMessage.id)}
                  >
                    <FiTrash2 />
                  </button>
                </div>

                <div className="message-meta">
                  <FiClock />
                  <span>{formatDate(selectedMessage.createdAt, 'EEEE dd MMMM yyyy à HH:mm')}</span>
                </div>

                <div className="message-body">
                  {selectedMessage.message}
                </div>

                <div className="message-actions">
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: Message depuis votre portfolio`}
                    className="btn btn-primary"
                  >
                    <FiMail />
                    Répondre par email
                  </a>
                </div>
              </div>
            ) : (
              <div className="no-message-selected">
                <FiMail />
                <p>Sélectionnez un message pour le lire</p>
              </div>
            )}
          </div>
        </div>

        {/* Modal de confirmation */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-icon danger">
                <FiTrash2 />
              </div>
              <h3>Supprimer ce message ?</h3>
              <p>Cette action est irréversible.</p>
              <div className="modal-actions">
                <button
                  className="btn btn-ghost"
                  onClick={() => setShowDeleteConfirm(null)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDelete(showDeleteConfirm)}
                >
                  <FiTrash2 />
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default MessagesManager;